import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { money, collectionLabel, colorHex, isLightColor } from "@/data/site";

// Reusable colour + size selector. Returns selected variant to parent.
export function VariantSelector({ product, color, setColor, size, setSize, compact = false }) {
  const variantFor = (c, s) => product.variants?.find((v) => v.color === c && v.size === s);
  const stockFor = (c, s) => variantFor(c, s)?.stock ?? 0;

  return (
    <div className="space-y-5">
      <div>
        <p className="overline-label mb-2">Colour · <span className="text-cream normal-case tracking-normal">{color}</span></p>
        <div className="flex flex-wrap items-center gap-3" data-testid="color-options">
          {product.colors?.map((c) => (
            <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`} aria-label={c} title={c}
              className={`relative h-9 w-9 rounded-full border transition-all duration-200 ${color === c ? "border-gold ring-2 ring-gold ring-offset-2 ring-offset-black scale-105" : "border-white/25 hover:border-gold/60 hover:scale-105"}`}
              style={{ backgroundColor: colorHex(c) }}>
              {color === c && <Check size={14} strokeWidth={3} className={`absolute inset-0 m-auto ${isLightColor(c) ? "text-black" : "text-white"}`} />}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="overline-label mb-2">Size {size && <span className="text-cream normal-case tracking-normal">· {size}</span>}</p>
        <div className="flex flex-wrap gap-2" data-testid="size-options">
          {product.sizes?.map((s) => {
            const oos = stockFor(color, s) === 0;
            return (
              <button key={s} onClick={() => !oos && setSize(s)} disabled={oos} data-testid={`size-${s}`}
                className={`min-w-[3rem] px-3 py-2 text-xs uppercase tracking-widest border transition ${size === s ? "border-gold bg-gold text-black" : oos ? "border-white/10 text-mutedgray/40 line-through cursor-not-allowed" : "border-white/15 text-cream/70 hover:border-gold/50"}`}>
                {s}
              </button>
            );
          })}
        </div>
        {size && <p className="mt-2 text-xs text-mutedgray" data-testid="variant-stock">{stockFor(color, size)} in stock</p>}
      </div>
    </div>
  );
}

export function QuickView() {
  const { quickView, setQuickView, addToCart, toggleWishlist, inWishlist } = useApp();
  const product = quickView;
  const [color, setColor] = useState(product?.colors?.[0]);
  const [size, setSize] = useState(null);

  React.useEffect(() => {
    if (product) { setColor(product.colors?.[0]); setSize(null); }
  }, [product]);

  if (!product) return null;
  const variant = product.variants?.find((v) => v.color === color && v.size === size);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="quickview-modal">
      <div className="absolute inset-0 bg-black/80" onClick={() => setQuickView(null)} />
      <div className="relative grid w-full max-w-3xl grid-cols-1 gap-0 border border-gold/25 bg-ink sm:grid-cols-2 reveal">
        <button onClick={() => setQuickView(null)} aria-label="Close" data-testid="quickview-close" className="absolute right-3 top-3 z-10 text-cream/70 hover:text-gold text-2xl leading-none">×</button>
        <div className="aspect-[3/4] overflow-hidden bg-black">
          <img src={product.images?.[0]} alt={product.name} className="h-full w-full object-cover" />
        </div>
        <div className="flex flex-col p-6 sm:p-8">
          <p className="overline-label mb-2">{collectionLabel(product.collection)}</p>
          <h3 className="font-serif text-2xl text-cream">{product.name}</h3>
          <div className="mt-2 flex items-center gap-3">
            <span className="font-mono text-lg text-gold">{money(product.price)}</span>
            {product.previous_price && <span className="font-mono text-sm text-mutedgray line-through">{money(product.previous_price)}</span>}
          </div>
          <p className="mt-4 line-clamp-3 text-sm text-cream/60">{product.description}</p>
          <div className="mt-6"><VariantSelector product={product} color={color} setColor={setColor} size={size} setSize={setSize} /></div>
          <div className="mt-auto flex gap-3 pt-6">
            <button onClick={() => { addToCart(product, variant, 1); setQuickView(null); }} data-testid="quickview-add"
              className="flex-1 bg-gold py-3 text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gold-bright">Add to bag</button>
            <button onClick={() => toggleWishlist(product)} aria-label="Wishlist"
              className={`px-4 border transition ${inWishlist(product.id) ? "border-gold bg-gold/10 text-gold" : "border-white/20 text-cream hover:border-gold"}`}>♡</button>
          </div>
          <Link to={`/product/${product.slug}`} onClick={() => setQuickView(null)} className="mt-3 text-center text-xs uppercase tracking-widest text-cream/50 hover:text-gold">View full details</Link>
        </div>
      </div>
    </div>
  );
}
